<template>
  <div class="relative w-full" ref="dropContainer">
    <div
      :class="[
        'custom-dropdown-trigger flex items-center justify-between px-3 py-1.5 rounded-xl border transition m-0',
        error ? 'border-red-600' : 'border-box-stroke',
        disabled || formAlreadySubmitted ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'
      ]"
      :tabindex="disabled || formAlreadySubmitted ? -1 : 0"
      ref="trigger"
    >
      <input
        ref="input"
        :value="searchQuery"
        @focus="openDropdown"
        @input="handleInput"
        @click="openDropdown"
        @keydown="handleKeyEvents"
        :disabled="disabled || formAlreadySubmitted"
        type="text"
        class="w-full bg-transparent outline-none border-none focus:ring-0 text-sm py-1.5"
        :placeholder="getDefaultSelectText"
        readonly
        :title="searchQuery"
      />
      <svg class="ml-2 w-4 h-4 text-gray-500 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24" @click="toggleDropDown">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <div
      v-if="open"
      class="custom-dropdown-list absolute left-0 z-50 bg-white border border-box-stroke rounded-xl shadow-lg overflow-auto"
      :style="dropdownDirection === 'down'
        ? { top: '100%', bottom: 'auto', minWidth: triggerWidth + 'px', maxHeight: dropdownMaxHeight + 'px' }
        : { top: 'auto', bottom: '100%', minWidth: triggerWidth + 'px', maxHeight: dropdownMaxHeight + 'px' }"
      ref="dropdown"
    >
      <div
        v-for="(option, idx) in filteredOptions"
        :key="idx"
        class="custom-dropdown-option px-3 py-2 cursor-pointer hover:bg-blue-500 hover:text-white" 
        :class="{ 'opacity-50': option.disabled, 'bg-blue-500 text-white': idx === highlightedIndex, }"
        @mousedown.prevent="selectOption(option)"
        :title="option.option_label"
      >
        <span class="whitespace-normal break-words">
          <!-- <template v-if="highlightedIndex === idx">
            <mark class="bg-blue-50 text-black">{{ option.option_label }}</mark>
          </template> -->
          <template>
            {{ option.option_label }}
          </template>
        </span>
      </div>
      <div v-if="!filteredOptions.length" class="px-3 py-2 text-gray-400">No matching options</div>
    </div>
  </div>
</template>

<script>

import { dynamicLocalizationSections } from "@shared/modules/localization/utils/caseEntityTranslation/constants";
import { translateFormEntity } from "../../utils/translation";
export default {
  name: "AutocompleteDropdown",
  props: {
    fieldData: {
      type: Object,
      required: true
    },
    error: Boolean,
    disabled: Boolean,
    formAlreadySubmitted: Boolean
  },
  data() {
    return {
      open: false,
      triggerWidth: 0,
      typedBuffer: '',
      typedTimeout: null,
      highlightedIndex: -1,
      dropdownMaxHeight: 240, // default fallback
      dropdownDirection: 'down' // can be 'down' or 'up'
    };
  },
  computed: {
    getOptions() {
      return (this.fieldData.options || []).map(opt => translateFormEntity(opt, dynamicLocalizationSections.OPTION));
    },
    getValue: {
      get() {
        return this.fieldData.value || '';
      },
      set(newValue) {
        this.$emit('input', newValue);
        this.$emit('handleFieldValueChnage', newValue);
      }
    },
    getDefaultSelectText() {
      return this.fieldData.placeholder || 'Select...';
    },
    filteredOptions() {
      return this.getOptions;
    },
    searchQuery() {
      return this.getOptions.find(opt => opt.option_value === this.getValue)?.option_label || '';
    }
  },
  watch: {
    getValue(newVal) {
      const match = this.getOptions.find(opt => opt.option_value === newVal);
      if (!this.open) {
        this.searchQuery = match ? match.option_label : '';
      }
    }
  },
  mounted() {
    this.setTriggerWidth();
    window.addEventListener('resize', this.setTriggerWidth);
    window.addEventListener('resize', this.setDropdownMaxHeight);
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.setTriggerWidth);
    window.removeEventListener('resize', this.setDropdownMaxHeight);
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (this.$refs?.dropContainer && !this.$refs?.dropContainer?.contains(event.target)) {
        this.open = false;
      } 
    },
    findScrollableContainer(el) {
      while (el && el !== document.body) {
        const overflowY = getComputedStyle(el).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') return el;
        el = el.parentElement;
      }
      return window;
    },
    setDropdownMaxHeight() {
      this.$nextTick(() => {
        const trigger = this.$refs.trigger;
        if (!trigger) return;

        const scrollContainer = this.findScrollableContainer(trigger);
        const triggerRect = trigger.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        const gap = 4;
        const spaceBelow = containerRect.bottom - triggerRect.bottom - gap;
        const spaceAbove = triggerRect.top - containerRect.top - gap;

        if (spaceBelow >= 300 || spaceBelow > spaceAbove) {
          this.dropdownDirection = 'down';
          this.dropdownMaxHeight = Math.max(spaceBelow, 100);
        } else {
          this.dropdownDirection = 'up';
          this.dropdownMaxHeight = Math.max(spaceAbove, 100);
        }
      });
    },
    setTriggerWidth() {
      this.$nextTick(() => {
        if (this.$refs.trigger) {
          this.triggerWidth = this.$refs.trigger.offsetWidth;
        }
      });
    },
    toggleDropDown() {
      this.open = !this.open;
    },
    openDropdown() {
      if (this.disabled || this.formAlreadySubmitted || this.open) return;
      this.open = true;
      this.setTriggerWidth();
      this.setDropdownMaxHeight();
      // this.$nextTick(() => {
      //   if (this.$refs.input) {
      //     this.$refs.input.select();
      //   }
      // });
    },
    handleInput() {
      this.open = true;
    },
    closeDropdown() {
      this.open = false;
      this.highlightedIndex = -1;
    },
    selectOption(option) {
      if (option.disabled) return;
      this.getValue = option.option_value;
      this.open = false;
      this.highlightedIndex = -1;
      // this.$nextTick(() => {
      //   if (this.$refs.input) {
      //     this.$refs.input.select();
      //   }
      // });
    },
    handleKeyEvents(event) {

      const options = this.filteredOptions;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.open = true;
        if (this.highlightedIndex < options.length - 1) {
          this.highlightedIndex++;
          this.scrollToHighlighted();
          const selected = options[this.highlightedIndex];
          if (selected && !selected.disabled) {
            this.getValue = selected.option_value;
          }
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
          this.scrollToHighlighted();
          const selected = options[this.highlightedIndex];
          if (selected && !selected.disabled) {
            this.getValue = selected.option_value;
          }
        }
      } else if (event.key === 'Enter' && this.highlightedIndex >= 0) {
        event.preventDefault();
        const selected = options[this.highlightedIndex];
        if (selected) this.selectOption(selected);
      } else if (event.key === 'Escape') {
        this.open = false;
      } else if (event.key === 'Tab') {
        if (this.highlightedIndex >= 0 && this.highlightedIndex < options.length) {
          this.selectOption(options[this.highlightedIndex]);
        }
        this.open = false;
      } else if (event.key.length === 1) {
        this.typedBuffer += event.key.toLowerCase();
        clearTimeout(this.typedTimeout);
        this.typedTimeout = setTimeout(() => {
          this.typedBuffer = '';
        }, 500);

        const matchIndex = options.findIndex(opt =>
          opt.option_label.toLowerCase().startsWith(this.typedBuffer)
        );

        if (matchIndex !== -1 && !options[matchIndex].disabled) {
          this.highlightedIndex = matchIndex;
          this.getValue = options[matchIndex].option_value;
          this.scrollToHighlighted();
          // this.$nextTick(() => {
          //   if (this.$refs.input) {
          //     this.$refs.input.select();
          //   }
          // });
        }
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const list = this.$refs.dropdown;
        const item = list?.children?.[this.highlightedIndex];
        if (item && list) {
          const listRect = list.getBoundingClientRect();
          const itemRect = item.getBoundingClientRect();
          if (itemRect.top < listRect.top) {
            list.scrollTop -= listRect.top - itemRect.top;
          } else if (itemRect.bottom > listRect.bottom) {
            list.scrollTop += itemRect.bottom - listRect.bottom;
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.custom-dropdown-trigger {
  outline: none;
}
.custom-dropdown-list {
  min-width: 100%;
  box-sizing: border-box;
}
.custom-dropdown-option {
  white-space: normal;
  word-break: break-word;
  border-radius: 0.5rem;
  transition: background 0.2s;
}
.custom-dropdown-option.bg-blue-100 {
  font-weight: bold;
}
.custom-dropdown-trigger:focus-within {
  border-color: #2563eb; /* Tailwind blue-600 */
  box-shadow: 0 0 0 1px #2563eb;
}
</style>
